
import React, { createContext, useContext, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type ViewingAsUser = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  role: string | null;
};

type ViewAsContextType = {
  viewAsUserId: string | null;
  setViewAsUser: (userId: string | null) => void;
  viewingAs: ViewingAsUser | null;
};

const ViewAsContext = createContext<ViewAsContextType | undefined>(undefined);

export function ViewAsProvider({ children }: { children: React.ReactNode }) {
  const [viewAsUserId, setViewAsUserId] = useState<string | null>(null);

  const { data: viewingAs } = useQuery<ViewingAsUser | null>({
    queryKey: ['view-as-user', viewAsUserId],
    queryFn: async (): Promise<ViewingAsUser | null> => {
      if (!viewAsUserId) return null;
      
      // Fetch profile data and role from user_roles in a single query
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          user_roles ( role )
        `)
        .eq('id', viewAsUserId)
        .single();
      
      if (error) {
        console.error("Error fetching viewingAs user profile with role:", error);
        return null;
      }

      if (!data) return null;

      // Extract role from the user_roles array (Supabase returns related table data as an array)
      // A user might have multiple roles in user_roles if not constrained,
      // but app_role enum suggests a primary one. We'll take the first.
      const role = data.user_roles && Array.isArray(data.user_roles) && data.user_roles.length > 0
                   ? data.user_roles[0].role
                   : null;
      
      return {
        id: data.id,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        email: data.email, // Assuming email is always present on profiles
        role: role,
      } as ViewingAsUser; // Ensure the returned object matches the type
    },
    enabled: !!viewAsUserId
  });

  return (
    <ViewAsContext.Provider value={{
      viewAsUserId,
      setViewAsUser: setViewAsUserId,
      viewingAs
    }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  const context = useContext(ViewAsContext);
  if (context === undefined) {
    throw new Error('useViewAs must be used within a ViewAsProvider');
  }
  return context;
}
